import React, { useEffect, useMemo, useState } from 'react';
import { Switch } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { useTheme } from '../../context/ThemeContext';
import {
  useAllCategoriesQuery,
  useAllCustomizationsQuery,
  useItemCategoriesQuery,
  useItemCustomizationsQuery,
} from './hooks/useItemsQuery';
import { useSubmitItem } from './hooks/useItemMutations';
import { Category, Customization, ItemFormModalProps, ItemFormValues } from './types';
import {
  CancelButton,
  CollapseChildrenWrap,
  CollapseSubtitle,
  CollapseTitle,
  CollapseWrapper,
  CollapseSectionLabel,
  FieldError,
  FixedTextArea,
  FormDivider,
  FormField,
  FormLabel,
  FormSectionTitle,
  FormSubmitButton,
  ModalFooter,
  ModalScrollContent,
  ModalWrapper,
  OptionalTag,
  RequiredMark,
  SettingsSection,
  SettingsSwitchGroup,
  SwitchDescription,
  SwitchLabel,
  SwitchRowItem,
} from './styles';
import ImageUploadField from './ImageUploadField';
import CustomizationsSection from './CustomizationsSection';
import CategoriesSection from './CategoriesSection';
import { Input } from 'antd';

const DEFAULT_VALUES: ItemFormValues = {
  item: '',
  description: '',
  price: '',
  taxable: true,
  is_make_it_a_meal: false,
};

const ItemFormModal: React.FC<ItemFormModalProps> = ({
  open,
  onClose,
  editingItem,
  onSuccess,
}) => {
  const { isDarkMode } = useTheme();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ItemFormValues>({
    defaultValues: editingItem
      ? {
          item: editingItem.item ?? '',
          description: editingItem.description ?? '',
          price: editingItem.price != null ? String(editingItem.price) : '',
          taxable: editingItem.taxable ?? true,
          is_make_it_a_meal: editingItem.is_make_it_a_meal ?? false,
        }
      : DEFAULT_VALUES,
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingPicture, setExistingPicture] = useState(editingItem?.picture ?? '');
  const [badgeImageFile, setBadgeImageFile] = useState<File | null>(null);
  const [existingBadgeImage, setExistingBadgeImage] = useState(editingItem?.badgeImage ?? '');
  const [selectedCustomizations, setSelectedCustomizations] = useState<Customization[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);

  const { data: allCustomizations = [], isLoading: isLoadingCustomizations } = useAllCustomizationsQuery(open);
  const { data: allCategories = [], isLoading: isLoadingCategories } = useAllCategoriesQuery(open);
  const { data: itemCustomizations } = useItemCustomizationsQuery(
    open && editingItem ? editingItem.id : null
  );
  const { data: itemCategories } = useItemCategoriesQuery(
    open && editingItem ? editingItem.id : null
  );

  const { mutate: submitItem, isPending } = useSubmitItem();

  useEffect(() => {
    if (!open || !editingItem) return;
    reset({
      item: editingItem.item ?? '',
      description: editingItem.description ?? '',
      price: editingItem.price != null ? String(editingItem.price) : '',
      taxable: editingItem.taxable ?? true,
      is_make_it_a_meal: editingItem.is_make_it_a_meal ?? false,
    });
    setExistingPicture(editingItem.picture ?? '');
    setExistingBadgeImage(editingItem.badgeImage ?? '');
  }, [editingItem?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (itemCustomizations) setSelectedCustomizations(itemCustomizations);
  }, [itemCustomizations]);

  useEffect(() => {
    if (itemCategories) setSelectedCategories(itemCategories);
  }, [itemCategories]);

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    setImageFile(null);
    setBadgeImageFile(null);
    setExistingPicture('');
    setExistingBadgeImage('');
    setSelectedCustomizations([]);
    setSelectedCategories([]);
    onClose();
  };

  const onSubmit = (values: ItemFormValues) => {
    submitItem(
      {
        formValues: values,
        imageFile,
        existingPicture,
        badgeImageFile,
        existingBadgeImage,
        selectedCustomizations,
        selectedCategories,
        editingItem,
      },
      {
        onSuccess: () => {
          handleClose();
          onSuccess();
        },
      }
    );
  };

  const collapseItems = useMemo(() => [
    {
      key: '1',
      label: (
        <div>
          <CollapseTitle>Customise Item</CollapseTitle>
          <br />
          <CollapseSubtitle $isDark={isDarkMode}>
            Add modifier groups, toppings, sides and categories
          </CollapseSubtitle>
        </div>
      ),
      children: (
        <CollapseChildrenWrap>
          <div>
            <CollapseSectionLabel $isDark={isDarkMode}>Customizations</CollapseSectionLabel>
            <CustomizationsSection
              allCustomizations={allCustomizations}
              selected={selectedCustomizations}
              onChange={setSelectedCustomizations}
              isDarkMode={isDarkMode}
              isLoading={isLoadingCustomizations}
            />
          </div>
          <div>
            <CollapseSectionLabel $isDark={isDarkMode}>Categories</CollapseSectionLabel>
            <CategoriesSection
              allCategories={allCategories}
              selected={selectedCategories}
              onChange={setSelectedCategories}
              isDarkMode={isDarkMode}
              isLoading={isLoadingCategories}
            />
          </div>
        </CollapseChildrenWrap>
      ),
    },
  ], [allCustomizations, isLoadingCustomizations, selectedCustomizations, allCategories, isLoadingCategories, selectedCategories, isDarkMode]);

  return (
    <ModalWrapper>
      <ModalScrollContent>

        <FormSectionTitle $isDark={isDarkMode}>Basic Information</FormSectionTitle>

        <FormField>
          <FormLabel $isDark={isDarkMode}>Item Name <RequiredMark /></FormLabel>
          <Controller
            name="item"
            control={control}
            rules={{ required: 'Item name is required' }}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="e.g. Penne Alla Vodka"
                status={errors.item ? 'error' : ''}
                size="large"
              />
            )}
          />
          {errors.item && <FieldError>{errors.item.message}</FieldError>}
        </FormField>

        <FormField>
          <FormLabel $isDark={isDarkMode}>Description <OptionalTag /></FormLabel>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <FixedTextArea
                {...field}
                placeholder="Describe the item (ingredients, allergens, etc.)"
                rows={2}
              />
            )}
          />
        </FormField>

        <FormField>
          <FormLabel $isDark={isDarkMode}>Price <RequiredMark /></FormLabel>
          <Controller
            name="price"
            control={control}
            rules={{
              required: 'Price is required',
              validate: (v) => !isNaN(Number(v)) || 'Price must be a number',
            }}
            render={({ field }) => (
              <Input
                {...field}
                type="number"
                placeholder="0.00"
                prefix="$"
                min={0}
                step={0.01}
                size="large"
                status={errors.price ? 'error' : ''}
              />
            )}
          />
          {errors.price && <FieldError>{errors.price.message}</FieldError>}
        </FormField>

        <FormDivider $isDark={isDarkMode} />

        <FormSectionTitle $isDark={isDarkMode}>Images</FormSectionTitle>

        <FormField>
          <FormLabel $isDark={isDarkMode}>Item Photo <OptionalTag /></FormLabel>
          <ImageUploadField
            imageFile={imageFile}
            existingPicture={existingPicture}
            isDarkMode={isDarkMode}
            onFileSelected={setImageFile}
            onDelete={() => {
              setImageFile(null);
              setExistingPicture('');
            }}
          />
        </FormField>

        <FormField>
          <FormLabel $isDark={isDarkMode}>Badge Image <OptionalTag /></FormLabel>
          <ImageUploadField
            imageFile={badgeImageFile}
            existingPicture={existingBadgeImage}
            isDarkMode={isDarkMode}
            onFileSelected={setBadgeImageFile}
            onDelete={() => {
              setBadgeImageFile(null);
              setExistingBadgeImage('');
            }}
          />
        </FormField>

        <FormDivider $isDark={isDarkMode} />

        <FormSectionTitle $isDark={isDarkMode}>Settings</FormSectionTitle>

        <SettingsSection $isDark={isDarkMode}>
          <SettingsSwitchGroup>
            <SwitchRowItem $hasDivider $isDark={isDarkMode}>
              <div>
                <SwitchLabel $isDark={isDarkMode}>Taxable</SwitchLabel>
                <SwitchDescription $isDark={isDarkMode}>Apply tax to this item</SwitchDescription>
              </div>
              <Controller
                name="taxable"
                control={control}
                render={({ field }) => (
                  <Switch checked={Boolean(field.value)} onChange={(checked) => field.onChange(checked)} />
                )}
              />
            </SwitchRowItem>
            <SwitchRowItem>
              <div>
                <SwitchLabel $isDark={isDarkMode}>Make it a Meal</SwitchLabel>
                <SwitchDescription $isDark={isDarkMode}>Allow customers to upgrade to a meal</SwitchDescription>
              </div>
              <Controller
                name="is_make_it_a_meal"
                control={control}
                render={({ field }) => (
                  <Switch checked={Boolean(field.value)} onChange={(checked) => field.onChange(checked)} />
                )}
              />
            </SwitchRowItem>
          </SettingsSwitchGroup>
        </SettingsSection>

        <FormDivider $isDark={isDarkMode} />

        <CollapseWrapper defaultActiveKey={[]} items={collapseItems} />

      </ModalScrollContent>

      <ModalFooter $isDark={isDarkMode}>
        <CancelButton size="large" onClick={handleClose}>
          Cancel
        </CancelButton>
        <FormSubmitButton
          type="primary"
          size="large"
          loading={isPending}
          onClick={handleSubmit(onSubmit)}
        >
          {editingItem ? 'Update Item' : 'Create Item'}
        </FormSubmitButton>
      </ModalFooter>
    </ModalWrapper>
  );
};

export default ItemFormModal;
